import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import * as XLSX from 'xlsx';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-dt';
import './QuarantineDepotsViewer.css';

// Data Context for sharing Excel data
const DataContext = React.createContext();

export const DataProvider = ({ children }) => {
    const [depotsData, setDepotsData] = useState([]);
    const [uniqueStates, setUniqueStates] = useState([]);
    const [uniqueClasses, setUniqueClasses] = useState([]);
    const [uniqueArrangementIds, setUniqueArrangementIds] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const dataLoaded = useRef(false);
    const isDev = process.env.NODE_ENV === 'development';

    const expectedHeaders = useMemo(() => [
        'State',
        'Suburb',
        'Approved arrangement class',
        'Arrangement ID #',
        'Biosecurity industry participant name',
        'Physical street',
        'Fumigation',
        'Tailgate',
        'Rural tailgate',
        'Cleaning',
        'Hot water treatment'
    ], []);

    useEffect(() => {
        if (dataLoaded.current) {
            if (isDev) {
                console.log('Data already loaded in context, skipping fetch.');
            }
            setIsLoading(false);
            return;
        }

        const loadDepotsData = async () => {
            try {
                setIsLoading(true);
                if (isDev) {
                    console.log('Fetching QuarantineDepots.xlsx in DataProvider...');
                }
                const response = await fetch('/QuarantineDepots.xlsx');
                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }
                const arrayBuffer = await response.arrayBuffer();
                if (isDev) {
                    console.log('Excel file fetched, parsing...');
                }
                const workbook = XLSX.read(arrayBuffer, { type: 'array' });
                const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
                if (!firstSheet) {
                    throw new Error('No sheets found in Excel file');
                }

                let loadedDepotsData = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
                if (isDev) {
                    console.log(`Parsed ${loadedDepotsData.length} rows from Excel`);
                }

                loadedDepotsData = loadedDepotsData.map((row) => {
                    const newRow = {};
                    expectedHeaders.forEach((header) => {
                        let value = row[header] !== undefined ? String(row[header]).trim() : '';
                        if (['Fumigation', 'Tailgate', 'Rural tailgate', 'Cleaning', 'Hot water treatment'].includes(header)) {
                            value = value.toLowerCase() === 'yes' ? 'Yes' : 'No';
                        }
                        newRow[header] = value;
                    });
                    return newRow;
                });

                const states = [...new Set(loadedDepotsData.map(row => row['State']).filter(Boolean))].sort();
                const arrangementIds = [...new Set(loadedDepotsData.map(row => row['Arrangement ID #']).filter(Boolean))].sort();
                const classes = [...new Set(
                    loadedDepotsData
                        .flatMap(row => String(row['Approved arrangement class']).replace(/\r\n/g, '\n').split('\n').map(cls => cls.trim()))
                        .filter(Boolean)
                )].sort((a, b) => {
                    const numA = Number(a);
                    const numB = Number(b);
                    if (!isNaN(numA) && !isNaN(numB)) {
                        return numA - numB;
                    }
                    return a.localeCompare(b);
                });

                if (isDev) {
                    console.log('Raw classes (before sorting):', [...new Set(
                        loadedDepotsData
                            .flatMap(row => String(row['Approved arrangement class']).replace(/\r\n/g, '\n').split('\n').map(cls => cls.trim()))
                            .filter(Boolean)
                    )]);
                    console.log('Sorted classes:', classes);
                    console.log('All Biosecurity industry participant names:', loadedDepotsData.map(row => row['Biosecurity industry participant name']));
                    console.log(`Loaded ${loadedDepotsData.length} depots into context`);
                    console.log('Unique states:', states);
                    console.log('Unique classes:', classes);
                    console.log('Unique arrangement IDs:', arrangementIds);
                    console.log('Sample rows:', JSON.stringify(loadedDepotsData.slice(0, 5), null, 2));
                }

                setDepotsData(loadedDepotsData);
                setUniqueStates(states);
                setUniqueClasses(classes);
                setUniqueArrangementIds(arrangementIds);
                dataLoaded.current = true;
                setIsLoading(false);
            } catch (error) {
                console.error('Error loading Quarantine Depots data:', error);
                alert('Failed to load Quarantine Depots data. Please check the console for details.');
                setDepotsData([]);
                setIsLoading(false);
            }
        };

        loadDepotsData();
    }, [expectedHeaders, isDev]);

    return (
        <DataContext.Provider value={{ depotsData, uniqueStates, uniqueClasses, uniqueArrangementIds, isLoading }}>
            {children}
        </DataContext.Provider>
    );
};

// Custom debounce hook
function useDebounce(callback, delay) {
    const timeoutRef = useRef(null);

    return useCallback((...args) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
            callback(...args);
        }, delay);
    }, [callback, delay]);
}

const QuarantineDepotsViewer = () => {
    const { depotsData, uniqueStates, uniqueClasses, uniqueArrangementIds, isLoading } = useContext(DataContext);
    const [isTableInitialized, setIsTableInitialized] = useState(false);
    const [stateFilter, setStateFilter] = useState('');
    const [classFilter, setClassFilter] = useState('');
    const [arrangementIdFilter, setArrangementIdFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [isProDropdownOpen, setIsProDropdownOpen] = useState(false);
    const dataTableRef = useRef(null);
    const tableRef = useRef(null);
    const navigate = useNavigate();
    const isDev = process.env.NODE_ENV === 'development';

    // Log component mount/unmount
    useEffect(() => {
        if (isDev) {
            console.log('QuarantineDepotsViewer mounted');
        }
        return () => {
            if (isDev) {
                console.log('QuarantineDepotsViewer unmounted');
            }
        };
    }, [isDev]);

    // Initialize DataTable
    useEffect(() => {
        if (!depotsData.length || !tableRef.current || isTableInitialized) return;

        try {
            if (isDev) {
                console.log('Initializing DataTable with', depotsData.length, 'rows');
            }
            dataTableRef.current = $(tableRef.current).DataTable({
                data: depotsData,
                columns: [
                    { title: 'State', data: 'State' },
                    { title: 'Suburb', data: 'Suburb' },
                    {
                        title: 'Approved arrangement class',
                        data: 'Approved arrangement class',
                        render: (data, type, row) => {
                            if (type === 'display' && data) {
                                // Replace newlines with <br /> for display
                                return String(data).replace(/\n/g, '<br />');
                            }
                            return data; // Return raw data for sorting/filtering
                        }
                    },
                    { title: 'Arrangement ID #', data: 'Arrangement ID #' },
                    { title: 'Biosecurity industry participant name', data: 'Biosecurity industry participant name' },
                    { title: 'Physical street', data: 'Physical street' },
                    { title: 'Fumigation', data: 'Fumigation' },
                    { title: 'Tailgate', data: 'Tailgate' },
                    { title: 'Rural tailgate', data: 'Rural tailgate' },
                    { title: 'Cleaning', data: 'Cleaning' },
                    { title: 'Hot water treatment', data: 'Hot water treatment' },
                ],
                columnDefs: [
                    { width: '100px', targets: 0 },
                    { width: '100px', targets: 1 },
                    { width: '150px', targets: 2 },
                    { width: '100px', targets: 3 },
                    { width: 'auto', targets: 4 },
                    { width: '150px', targets: 5 },
                    { width: '80px', targets: 6 },
                    { width: '80px', targets: 7 },
                    { width: '80px', targets: 8 },
                    { width: '80px', targets: 9 },
                    { width: '80px', targets: 10 },
                ],
                pageLength: 10,
                lengthMenu: [10, 25, 50, 100],
                searching: true, // Enable searching for custom filters
                ordering: true,
                order: [],
                language: {
                    emptyTable: 'No data available',
                    loadingRecords: 'Loading...',
                    processing: 'Processing...',
                    lengthMenu: 'Show _MENU_ entries',
                },
                dom: '<"table-header"l>t<"table-footer"ip>',
                deferRender: true,
                responsive: true,
                destroy: true,
            });

            // Log column indices and data sample
            if (isDev) {
                console.log('DataTable columns:', dataTableRef.current.settings()[0].aoColumns.map(col => col.data));
                console.log('DataTable data sample:', JSON.stringify(dataTableRef.current.data().toArray().slice(0, 5), null, 2));
            }

            setIsTableInitialized(true);
            if (isDev) {
                console.log('DataTable initialized');
            }
        } catch (error) {
            console.error('Error initializing DataTable:', error);
            alert('Failed to initialize the table. Please check the console for details.');
        }

        return () => {
            if (dataTableRef.current && !document.getElementById('quarantineDepotsTable')) {
                try {
                    dataTableRef.current.destroy();
                    if (isDev) {
                        console.log('DataTable destroyed on cleanup');
                    }
                } catch (error) {
                    console.error('Error destroying DataTable:', error);
                }
            }
        };
    }, [depotsData, isTableInitialized, isDev]);

    // Handle click outside for dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            const dropdown = document.querySelector('.pro-dropdown');
            if (dropdown && !dropdown.contains(event.target)) {
                setIsProDropdownOpen(false);
            }
        };

        if (isProDropdownOpen) {
            document.addEventListener('click', handleClickOutside, { capture: true });
        }

        return () => {
            document.removeEventListener('click', handleClickOutside, { capture: true });
        };
    }, [isProDropdownOpen]);

    // Apply filters
    const applyFilters = useDebounce(() => {
        if (!dataTableRef.current || !isTableInitialized) return;

        if (isDev) {
            console.log('Applying filters:', { stateFilter, classFilter, arrangementIdFilter, nameFilter });
            console.log('Checking filter function execution...');
        }

        $.fn.dataTable.ext.search = [];

        const filterFunction = (settings, data, dataIndex) => {
            if (settings.nTable.id !== 'quarantineDepotsTable') {
                if (isDev) {
                    console.log('Skipping filter for non-matching table ID:', settings.nTable.id);
                }
                return true;
            }

            const state = String(data[0] || '').toLowerCase().trim();
            const approvedClass = String(data[2] || '').toLowerCase().trim().replace(/\r\n/g, '\n');
            const arrangementId = String(data[3] || '').toLowerCase().trim();
            const name = String(data[4] || '').toLowerCase().trim();

            const stateFilterValue = stateFilter.toLowerCase().trim();
            const classFilterValue = classFilter.toLowerCase().trim();
            const arrangementIdFilterValue = arrangementIdFilter.toLowerCase().trim();
            const nameFilterValue = nameFilter.toLowerCase().trim();

            const stateMatch = !stateFilterValue || state === stateFilterValue;
            const classMatch = !classFilterValue || approvedClass.includes(classFilterValue);
            const arrangementIdMatch = !arrangementIdFilterValue || arrangementId === arrangementIdFilterValue;
            const nameMatch = !nameFilterValue || name.includes(nameFilterValue);

            const isMatch = stateMatch && classMatch && arrangementIdMatch && nameMatch;

            if (isDev) {
                console.log(`Row ${dataIndex}:`, {
                    arrangementId: data[3],
                    state,
                    approvedClass,
                    name,
                    stateMatch,
                    classMatch,
                    arrangementIdMatch,
                    nameMatch,
                    isMatch
                });
            }

            return isMatch;
        };

        $.fn.dataTable.ext.search.push(filterFunction);

        try {
            dataTableRef.current.draw();
            if (isDev) {
                console.log('DataTable filters applied');
            }
        } catch (error) {
            console.error('Error during DataTable filter draw:', error);
            // Fallback: Manually filter data
            const filteredData = depotsData.filter((row, index) => {
                const state = String(row['State'] || '').toLowerCase().trim();
                const approvedClass = String(row['Approved arrangement class'] || '').toLowerCase().trim().replace(/\r\n/g, '\n');
                const arrangementId = String(row['Arrangement ID #'] || '').toLowerCase().trim();
                const name = String(row['Biosecurity industry participant name'] || '').toLowerCase().trim();

                const stateFilterValue = stateFilter.toLowerCase().trim();
                const classFilterValue = classFilter.toLowerCase().trim();
                const arrangementIdFilterValue = arrangementIdFilter.toLowerCase().trim();
                const nameFilterValue = nameFilter.toLowerCase().trim();

                const stateMatch = !stateFilterValue || state === stateFilterValue;
                const classMatch = !classFilterValue || approvedClass.includes(classFilterValue);
                const arrangementIdMatch = !arrangementIdFilterValue || arrangementId === arrangementIdFilterValue;
                const nameMatch = !nameFilterValue || name.includes(nameFilterValue);

                const isMatch = stateMatch && classMatch && arrangementIdMatch && nameMatch;

                if (isDev) {
                    console.log(`Fallback filter row ${index}:`, {
                        arrangementId,
                        state,
                        approvedClass,
                        name,
                        stateMatch,
                        classMatch,
                        arrangementIdMatch,
                        nameMatch,
                        isMatch
                    });
                }

                return isMatch;
            });

            if (isDev) {
                console.log('Fallback filtered rows count:', filteredData.length);
            }

            try {
                dataTableRef.current.clear().rows.add(filteredData).draw();
                if (isDev) {
                    console.log('Fallback filter applied to DataTable');
                }
            } catch (fallbackError) {
                console.error('Error applying fallback filter:', fallbackError);
            }
        }
    }, 400);

    useEffect(() => {
        applyFilters();
    }, [stateFilter, classFilter, arrangementIdFilter, nameFilter, isTableInitialized, applyFilters]);

    // Filter change handlers
    const handleStateFilterChange = useCallback((e) => {
        setStateFilter(e.target.value);
    }, []);

    const handleClassFilterChange = useCallback((e) => {
        setClassFilter(e.target.value);
    }, []);

    const handleArrangementIdFilterChange = useCallback((e) => {
        setArrangementIdFilter(e.target.value);
    }, []);

    const handleNameFilterChange = useCallback((e) => {
        setNameFilter(e.target.value);
    }, []);

    // Reset filters
    const resetTable = useCallback(() => {
        setStateFilter('');
        setClassFilter('');
        setArrangementIdFilter('');
        setNameFilter('');
        if (dataTableRef.current) {
            $.fn.dataTable.ext.search = [];
            dataTableRef.current.clear().rows.add(depotsData).draw();
            dataTableRef.current.page(0).draw('page');
            if (isDev) {
                console.log('Filters reset');
            }
        }
    }, [depotsData, isDev]);

    // Handle logout
    const handleLogout = () => {
        signOut(auth)
            .then(() => {
                navigate('/');
            })
            .catch((err) => console.error('Logout error:', err));
    };

    return (
        <div className="quarantine-depots-viewer-container">
            <header className="header">
                <div className="header-content">
                    <img src="/logo.png" alt="Logo" />
                    <h1>Quarantine Depots</h1>
                    <nav className="nav">
                        <Link to="/" className="nav-item">Home</Link>
                        <Link to="/app" className="nav-item">Classify Goods</Link>
                        <Link to="/customs-chat" className="nav-item">Customs Chat</Link>
                        <div
                            className={`nav-item pro-dropdown ${isProDropdownOpen ? 'open' : ''}`}
                            onClick={() => setIsProDropdownOpen(!isProDropdownOpen)}
                            role="button"
                            aria-expanded={isProDropdownOpen}
                            aria-controls="pro-dropdown-menu"
                        >
                            <span>Pro</span>
                            <div className="dropdown-content" id="pro-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                                <Link to="/tariff-viewer" className="dropdown-item">Tariff Viewer</Link>
                                <Link to="/tco-viewer" className="dropdown-item">TCO Viewer</Link>
                                <Link to="/precedent-viewer" className="dropdown-item">Precedent Viewer</Link>
                                <Link to="/ahecc-viewer" className="dropdown-item">AHECC Viewer</Link>
                                <Link to="/schedule4-viewer" className="dropdown-item">Schedule 4 Viewer</Link>
                                <Link to="/refund-reason-viewer" className="dropdown-item">Refund Reason Codes</Link>
                                <Link to="/gst-exemptions-viewer" className="dropdown-item">GST Exemption Codes</Link>
                                <Link to="/customs-depots-viewer" className="dropdown-item">Customs Depots Viewer</Link>
                                <Link to="/quarantine-depots-viewer" className="dropdown-item active">Quarantine Depots Viewer</Link>
                                <Link to="/history" className="dropdown-item">History</Link>
                            </div>
                        </div>
                        <Link to="/settings" className="nav-item">Settings</Link>
                        <span className="nav-item logout" onClick={handleLogout}>Logout</span>
                    </nav>
                </div>    
            </header>
            <main className="main-content">
                <div className="table-panel">
                    {isLoading ? (
                        <div className="loading">Loading...</div>
                    ) : (
                        <>
                            <div className="table-header">
                                <div id="customFilter">
                                    <select
                                        value={stateFilter}
                                        onChange={handleStateFilterChange}
                                        className="custom-filter-select"
                                    >
                                        <option value="">All States</option>
                                        {uniqueStates.map((state) => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={classFilter}
                                        onChange={handleClassFilterChange}
                                        className="custom-filter-select"
                                    >
                                        <option value="">All Classes</option>
                                        {uniqueClasses.map((cls) => (
                                            <option key={cls} value={cls}>{cls}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={arrangementIdFilter}
                                        onChange={handleArrangementIdFilterChange}
                                        className="custom-filter-select"
                                    >
                                        <option value="">All Arrangement IDs</option>
                                        {uniqueArrangementIds.map((id) => (
                                            <option key={id} value={id}>{id}</option>
                                        ))}
                                    </select>
                                    <input
                                        type="text"
                                        value={nameFilter}
                                        onChange={handleNameFilterChange}
                                        placeholder="Filter Biosecurity Name..."
                                        className="custom-filter-input name-filter-input"
                                    />
                                </div>
                                <div className="table-buttons">
                                    <button id="resetTableBtn" onClick={resetTable}>
                                        Reset Table
                                    </button>
                                </div>
                            </div>
                            <table id="quarantineDepotsTable" className="display" ref={tableRef}></table>
                        </>
                    )}
                </div>
            </main>
        </div>
    );
};

export default React.memo(QuarantineDepotsViewer);